import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { findFirstErrorMessage } from "../../shared/utils/form/find-first-error-message";
import {
  CriarClienteFormData,
  criarClienteSchema,
} from "./schema/criar-empresa.schema";
import { useTheme } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { ContatoFormPart } from "../../shared/components/formulario/parts/ContatoFormPart/contato-form.part";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import {
  limpaCamposPsFisica,
  limpaCamposPsJuridica,
} from "../../shared/utils/limpa-campo-form/limpa-campos.part";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { useMutation } from "@tanstack/react-query";
import { clienteService } from "./service/cliente.service";
import useLoadingStore from "../../shared/store/loading.store";
import { toast } from "react-toastify";
import { ICnpjData } from "../../shared/components/buscarCNPJ/buscar-cnpj";

export function CriarCliente() {
  const methods = useForm<CriarClienteFormData>({
    resolver: yupResolver(criarClienteSchema),
    defaultValues: {
      active: true,
      cnpj: "",
      cpf: "",
    },
  });

  const {
    handleSubmit,
    setValue,
    control,
    setError,
    formState: { errors },
  } = methods;

  const navigate = useNavigate();
  const [valueOpcao, setOpicao] = useState("cnpj");
  const { setLoading, stopLoading } = useLoadingStore();

  // Exibe o primeiro erro de validação quando o submit é bloqueado pelo schema
  const onInvalidSubmit = (formErrors: typeof errors) => {
    const primeiraMensagem = findFirstErrorMessage(formErrors);
    toast.error(primeiraMensagem || "Existem campos inválidos no formulário.");
  };

  // Limpa campos ao trocar opção
  useEffect(() => {
    if (valueOpcao != "cnpj") {
      limpaCamposPsJuridica({ methods });
    } else {
      limpaCamposPsFisica({ methods });
    }
  }, [valueOpcao, methods]);

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: CriarClienteFormData) => {
      setLoading(true, "Estamos quase lá! Só mais um momento...");
      await clienteService.criar(data);
    },
    onSuccess: () => {
      stopLoading();
      toast.success("Cliente criada com sucesso");
      navigate("/cliente", { state: { reload: true } });
    },
    onError: (err: any) => {
      stopLoading();
      toast.error(err.mensagem || "Erro ao criar cliente.");
    },
  });

  const preencherCampos = (dados: ICnpjData) => {
    setValue("fantasyName", dados.fantasia || dados.razaoSocial || "");
    setValue("corporateName", dados.razaoSocial || "");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpicao(event.target.value);
  };

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <BaseLayout title="Criar Cliente">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => {
          mutate(formData);
        }, onInvalidSubmit)}
        aoClicarBotaoVoltar={() => navigate("/cliente")}
      />
      <Box
        component="form"
        onSubmit={handleSubmit((formData) => {
          mutate(formData);
          navigate("/cliente");
        }, onInvalidSubmit)}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        <Box padding="20px 0">
          <Typography variant="h5" padding="20px 0">
            Dados do Cliente:
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
            gap="20px"
            width="100%"
            flexWrap="wrap"
          >
            <FormControl>
              <FormLabel>Selecione CPF ou CNPJ</FormLabel>
              <RadioGroup value={valueOpcao} onChange={handleChange} row>
                <FormControlLabel
                  value="cnpj"
                  control={<Radio />}
                  label="CNPJ"
                />
                <FormControlLabel value="cpf" control={<Radio />} label="CPF" />
              </RadioGroup>
            </FormControl>

            {valueOpcao === "cnpj" ? (
              <PessoaJuridicaFormPart
                methods={methods}
                preencherCampos={preencherCampos}
              />
            ) : (
              <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <ContatoFormPart methods={methods} mdDown={mdDown} />

          <Divider sx={{ my: 2 }} />

          <EnderecoFormPart methods={methods} mdDown={mdDown} />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h5" padding="20px 0">
            Status:
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
            gap="20px"
            width="100%"
            flexWrap="wrap"
          >
            <Controller
              name="active"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <FormControl>
                  <FormLabel>Empresa ativada</FormLabel>
                  <RadioGroup
                    row
                    value={field.value ? "true" : "false"}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Ativa"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="Desativada"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Box>
        </Box>
      </Box>
    </BaseLayout>
  );
}
